import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
// oxlint-disable-next-line no-restricted-imports
import * as Sentry from '@sentry/react';
import { StatsigProvider } from '@statsig/react-bindings';
import { createRootRouteWithContext, Outlet, redirect } from '@tanstack/react-router';
import { getStatsigClient, setStatsigClient } from '@/api/statsig-client';
import { AnalyticsProvider } from '@/components/Analytics/AnalyticsProvider';
import { AnalyticsRoot } from '@/components/Analytics/AnalyticsRoot';
import { initialUtmAttribution } from '@/components/Analytics/tracking';
import { GlobalAlertProvider } from '@/components/Common/GlobalAlert';
import { DefaultErrorFallback } from '@/components/Error';
import { OneTrustProvider } from '@/contexts/onetrust-context';
import { SupportProvider } from '@/contexts/support-context';
import { DevTools } from '@/helpers/DevTools/DevTools';
import { createStatsigClient } from '@/helpers/statsig';
import { head, Head } from '@/router/Head';
import { type RootRouteContext } from '@/router/index.types';
import { rootSearchSchema } from '@/router/utils';
import { persistSignupUrl, persistUrlParamsFromSearch } from '@/utils/analytics-utils';
import { storeInitialReferrer } from '@/utils/referrer';

/** Where a visitor enters the login flow: `/` redirects to `/login`, and `/signup` lands on the same page. */
const AUTH_ENTRY_PATHS: ReadonlySet<string> = new Set(['/', '/login', '/signup']);

export const Route = createRootRouteWithContext<RootRouteContext>()({
  validateSearch: rootSearchSchema,
  staticData: {
    title: 'Qdrant Cloud',
  },
  beforeLoad: async ({ location, search }) => {
    // Persist referrer before any redirects can erase it
    storeInitialReferrer();

    // The signup event reports the URL the visitor arrived on, UTMs included, so it is saved
    // before the redirect below strips them.
    if (AUTH_ENTRY_PATHS.has(location.pathname)) {
      persistSignupUrl(location.href);
    }

    const { shouldCleanupUtmSearch, utmParams, hubspotParams } = persistUrlParamsFromSearch(search);

    if (shouldCleanupUtmSearch) {
      initialUtmAttribution();
      // Clean up url from UTM params.
      throw redirect({
        to: '.',
        search: (prev) => ({
          ...prev,
          ...hubspotParams,
          ...(utmParams
            ? Object.keys(utmParams).reduce(
                (acc, key) => {
                  acc[key] = undefined;
                  return acc;
                },
                {} as Record<string, undefined>,
              )
            : null),
          sugerEntitlementId: undefined,
          partner: undefined,
        }),
      });
    }

    if (!getStatsigClient()) {
      const statsigClient = createStatsigClient(search);
      setStatsigClient(statsigClient);

      // Surface active feature gates on Sentry errors. Registered here rather than at
      // Sentry.init (src/sentry.ts) because the Statsig client is created lazily, after init runs.
      // The integration listens for `gate_evaluation`, so it must be attached before any gate check.
      Sentry.addIntegration(Sentry.statsigIntegration({ featureFlagClient: statsigClient }));

      await statsigClient.initializeAsync();
    }

    return { statsigClient: getStatsigClient() };
  },
  component: RootComponent,
  errorComponent: DefaultErrorFallback,
  head,
});

function RootComponent() {
  const client = Route.useRouteContext({ select: (s) => s.statsigClient });

  return (
    <>
      <Head />
      <GTMProvider state={{ id: window.__QDRANT_CLOUD__.gtm_id }}>
        <OneTrustProvider>
          <StatsigProvider client={client}>
            <AnalyticsProvider>
              <GlobalAlertProvider>
                <AnalyticsRoot>
                  <SupportProvider>
                    <Outlet />
                  </SupportProvider>
                </AnalyticsRoot>
              </GlobalAlertProvider>
            </AnalyticsProvider>
            <DevTools />
          </StatsigProvider>
        </OneTrustProvider>
      </GTMProvider>
    </>
  );
}
